import React, { useState, useCallback, useEffect } from 'react';
import ChatSidebar from './ChatSidebar';
import ChatMainPanel from './ChatMainPanel';
import { useAuth } from '../../hooks/useAuth';
import styles from './ChatLayout.module.css';
// Split UI removed; sidebar behavior independent

interface ChatLayoutProps {
  children: React.ReactNode;
}

const ChatLayout: React.FC<ChatLayoutProps> = ({
  children
}) => {
  const { user } = useAuth();
  const [isMobile, setIsMobile] = useState(false);
  const [isSidebarLocked, setIsSidebarLocked] = useState(false);
  const [isSidebarHovered, setIsSidebarHovered] = useState(false);

  // Responsive breakpoint detection
  useEffect(() => {
    const checkScreenSize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // No split coupling: keep sidebar logic standalone


  // Calculate effective sidebar width (expanded when locked OR hovered)
  const effectiveSidebarWidth = isSidebarLocked || isSidebarHovered ? '300px' : '72px';
  // Fixed overlays should not move on hover; only lock changes this value
  const fixedSidebarLeft = isSidebarLocked ? '300px' : '72px';

  return (
    <div 
      className={`${styles.chatLayout} ${styles.sidebarCollapsed} ${isMobile ? styles.mobile : ''}`}
      style={{
        '--sidebar-width': effectiveSidebarWidth,
        '--chat-lane-width': '60rem',
        '--sidebar-fixed-left': fixedSidebarLeft,
        // Split defaults (overridden by ChatPage when open)
        '--split-right-width': '0vw',
        '--split-gap': '24px',
        '--lane-gap': '2rem',
        '--fixed-right-offset': '0px',
        // Unsplit max lane width for composers
        '--chat-lane-max': '60rem',
        '--lane-side-gutter': '2rem',
        '--lane-left-gutter': '2rem',
        '--lane-right-gutter': '2rem',
      } as React.CSSProperties}
    >
      {/* Left Sidebar - Always collapsed */}
      <aside className={`${styles.sidebar} ${styles.collapsed} ${isSidebarLocked ? styles.locked : ''}`}>
        <ChatSidebar 
          user={user}
          onLockChange={setIsSidebarLocked}
          onHoverChange={setIsSidebarHovered}
          forceCollapse={false}
          disableHover={false}
        />
      </aside>

      {/* Main Content Area */}
      <main className={styles.main}>
        <ChatMainPanel>
          {children}
        </ChatMainPanel>
      </main>
    </div>
  );
};

export default ChatLayout;
